<!-- src/views/projects/SingleItemInspectionFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & PROTOCOL METADATA -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden text-left">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none"><ShieldCheckIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <span class="px-3 py-1 bg-[#20FCF1]/10 text-[#20FCF1] border border-[#20FCF1]/30 rounded-full text-[9px] font-black uppercase tracking-widest">Protocol SS-PEI-1</span>
                <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">MSPRG010 Rev 2.0</span>
              </div>
              <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none">Pre-Use Plant & Equipment Inspection</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Single-Item 7-Day Weekly Checklist • Active Worksite Log</p>
            </div>

            <div class="bg-white/5 p-4 rounded-2xl border border-white/10 flex items-center gap-4">
              <div v-if="qrCodeUrl" class="h-16 w-16 bg-white p-1 rounded-xl shrink-0 flex items-center justify-center">
                <img :src="qrCodeUrl" alt="QR" class="w-full h-full object-contain" />
              </div>
              <div>
                <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">Active Record</p>
                <p class="text-sm font-black text-white uppercase mt-1">{{ formReference }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. FORM BODY -->
        <div class="bg-white p-6 sm:p-10 md:p-14 rounded-b-[40px] shadow-xl border-x border-b border-slate-100 text-left">
          
          <form @submit.prevent="submitForm" class="space-y-16">

            <!-- SECTION 1: ITEM DETAILS -->
            <section class="space-y-6">
              <div class="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div class="bg-[#20FCF1] text-[#0A1630] w-10 h-10 rounded-xl flex items-center justify-center font-black">1</div>
                <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Item Details & Operational Context</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
                <div>
                  <label class="form-label">Asset ID *</label>
                  <!-- 🛡️ DYNAMIC OPTIONS: Populated from Admin Manage Options registry -->
                  <select v-model="formData.itemDetails.assetId" @change="onAssetSelected" required class="form-input font-black uppercase cursor-pointer">
                    <option value="">Select Managed Asset...</option>
                    <option v-for="asset in availableAssets" :key="asset.value" :value="asset.value">
                      {{ asset.value }} - {{ asset.text }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="form-label">Week Commencing Date *</label>
                  <input type="date" v-model="formData.itemDetails.weekCommencing" required class="form-input" />
                </div>
                <div>
                  <label class="form-label">Description *</label>
                  <input type="text" v-model="formData.itemDetails.description" required placeholder="e.g. Trimble S9 Total Station" class="form-input" />
                </div>
                <div>
                  <label class="form-label">Serial / CAT No.</label>
                  <input type="text" v-model="formData.itemDetails.serialNo" placeholder="Serial Number" class="form-input" />
                </div>
                <div>
                  <label class="form-label">Manufacturer / Model</label>
                  <input type="text" v-model="formData.itemDetails.manufacturer" placeholder="Manufacturer" class="form-input" />
                </div>
                <div>
                  <label class="form-label">Category</label>
                  <select v-model="formData.itemDetails.category" class="form-input">
                    <option value="Survey">Survey</option>
                    <option value="Track Equipment">Track Equipment</option>
                    <option value="Electrical / Safety">Electrical / Safety</option>
                    <option value="Power Tool">Power Tool</option>
                    <option value="Hand Tool">Hand Tool</option>
                    <option value="Survey Accessory">Survey Accessory</option>
                  </select>
                </div>
                <div>
                  <label class="form-label">Site / Location *</label>
                  <input type="text" v-model="formData.itemDetails.siteLocation" required placeholder="Project Site" class="form-input" />
                </div>
                <div>
                  <label class="form-label">Risk Rating</label>
                  <select v-model="formData.itemDetails.riskRating" class="form-input">
                    <option value="Low">Low Risk</option>
                    <option value="Medium">Medium Risk</option>
                    <option value="High">High Risk</option>
                  </select>
                </div>
                <div>
                  <label class="form-label">Primary Inspector *</label>
                  <input type="text" v-model="formData.itemDetails.primaryInspector" required placeholder="Inspector Name" class="form-input" />
                </div>
              </div>

              <div class="flex items-center gap-4 p-5 bg-[#EBFDF8] rounded-2xl border border-[#20FCF1]/30">
                <span class="text-xs font-black text-[#0A1630] uppercase">Competence Confirmed & Authorised?</span>
                <div class="flex gap-4">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" v-model="formData.itemDetails.competenceConfirmed" :value="true" class="h-4 w-4 text-[#0A1630]" />
                    <span class="text-xs font-bold text-slate-700">Yes</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" v-model="formData.itemDetails.competenceConfirmed" :value="false" class="h-4 w-4 text-rose-600" />
                    <span class="text-xs font-bold text-slate-700">No</span>
                  </label>
                </div>
              </div>
            </section>

            <!-- SECTION 2: DAILY INSPECTION CHECKLIST (7-DAY MATRIX) -->
            <section class="space-y-6">
              <div class="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div class="bg-[#20FCF1] text-[#0A1630] w-10 h-10 rounded-xl flex items-center justify-center font-black">2</div>
                <div>
                  <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Daily Inspection Checklist (Monday – Sunday)</h2>
                  <p class="text-xs text-slate-500 font-medium">Tick under the day the check was completed and found satisfactory. Record comments in the final column.</p>
                </div>
              </div>

              <div class="overflow-x-auto rounded-[32px] border border-slate-200 shadow-sm bg-white">
                <table class="min-w-full divide-y divide-slate-200 text-left">
                  <thead class="bg-[#0A1630] text-white">
                    <tr>
                      <th class="px-6 py-4 text-[10px] font-black text-[#20FCF1] uppercase tracking-widest w-[38%]">Check Item (Tick if OK)</th>
                      <th v-for="day in daysOfWeek" :key="day" class="px-3 py-4 text-[10px] font-black text-center uppercase tracking-widest w-[6%]">{{ day }}</th>
                      <th class="px-6 py-4 text-[10px] font-black text-[#20FCF1] uppercase tracking-widest w-[20%]">Initials / Comments</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100">
                    <tr v-for="(checkItem, idx) in checklistItems" :key="idx" class="hover:bg-slate-50 transition-colors">
                      <td class="px-6 py-4 text-xs font-bold text-slate-700 uppercase">
                        {{ idx + 1 }}. {{ checkItem }}
                      </td>
                      <td v-for="day in daysOfWeek" :key="day" class="px-3 py-4 text-center">
                        <input 
                          type="checkbox" 
                          v-model="formData.checklist[idx][day.toLowerCase()]" 
                          class="h-5 w-5 text-[#0A1630] border-slate-300 rounded focus:ring-[#20FCF1] cursor-pointer" 
                        />
                      </td>
                      <td class="px-4 py-3">
                        <input type="text" v-model="formData.checklist[idx].comments" placeholder="Initials / notes..." class="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium text-slate-700 outline-none focus:ring-1 focus:ring-[#20FCF1]" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <!-- SECTION 3: DAILY OVERALL RESULT -->
            <section class="space-y-6">
              <div class="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div class="bg-[#20FCF1] text-[#0A1630] w-10 h-10 rounded-xl flex items-center justify-center font-black">3</div>
                <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Daily Overall Result & Quarantine Trigger</h2>
              </div>

              <div class="overflow-x-auto rounded-[32px] border border-slate-200 shadow-sm bg-white">
                <table class="min-w-full divide-y divide-slate-200 text-left">
                  <thead class="bg-[#0A1630] text-white">
                    <tr>
                      <th class="px-6 py-4 text-[10px] font-black text-[#20FCF1] uppercase tracking-widest w-[40%]">Daily Result Status</th>
                      <th v-for="day in daysOfWeek" :key="day" class="px-3 py-4 text-[10px] font-black text-center uppercase tracking-widest w-[8%]">{{ day }}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100">
                    <tr class="hover:bg-emerald-50/50">
                      <td class="px-6 py-4 text-xs font-black text-emerald-700 uppercase">PASS – Safe to use</td>
                      <td v-for="day in daysOfWeek" :key="day" class="px-3 py-4 text-center">
                        <input type="radio" :name="`result_${day}`" v-model="formData.dailyResults[day.toLowerCase()]" value="PASS" class="h-4 w-4 text-emerald-600 cursor-pointer" />
                      </td>
                    </tr>
                    <tr class="hover:bg-rose-50/50">
                      <td class="px-6 py-4 text-xs font-black text-rose-700 uppercase">FAIL – Do not use</td>
                      <td v-for="day in daysOfWeek" :key="day" class="px-3 py-4 text-center">
                        <input type="radio" :name="`result_${day}`" v-model="formData.dailyResults[day.toLowerCase()]" value="FAIL" class="h-4 w-4 text-rose-600 cursor-pointer" />
                      </td>
                    </tr>
                    <tr class="hover:bg-amber-50/50">
                      <td class="px-6 py-4 text-xs font-black text-amber-700 uppercase">QUARANTINE (Withdraw & Tag)</td>
                      <td v-for="day in daysOfWeek" :key="day" class="px-3 py-4 text-center">
                        <input type="radio" :name="`result_${day}`" v-model="formData.dailyResults[day.toLowerCase()]" value="QUARANTINE" class="h-4 w-4 text-amber-600 cursor-pointer" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <!-- SECTION 4: DEFECTS FOUND / ACTIONS TAKEN -->
            <section class="space-y-6">
              <div class="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div class="bg-[#20FCF1] text-[#0A1630] w-10 h-10 rounded-xl flex items-center justify-center font-black">4</div>
                <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Defects Found / Actions Taken</h2>
              </div>
              <div>
                <textarea v-model="formData.defectsDetails" rows="4" placeholder="Detail any defects found, quarantine actions, or repairs required..." class="form-input"></textarea>
              </div>
              <div class="p-6 bg-slate-50 rounded-3xl border border-slate-200">
                <DocumentUploader id="eq_defect_evidence" label="Upload Defect Photo / Quarantine Tag Evidence" @update:files="(files) => handleFileChange('defectEvidence', files)" />
              </div>
            </section>

            <!-- SECTION 5: WEEKLY SIGN-OFF -->
            <section class="bg-slate-900 p-8 md:p-12 rounded-[40px] text-white shadow-2xl space-y-8">
              <h2 class="text-xl font-black text-[#20FCF1] uppercase tracking-tight">5. Weekly Sign-Off & Authorisation</h2>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label class="text-[10px] font-black uppercase text-[#20FCF1] mb-2 block">Inspector Name (Print) *</label>
                  <input type="text" v-model="formData.signOff.inspectorName" required class="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white font-bold uppercase" />
                </div>
                <div>
                  <label class="text-[10px] font-black uppercase text-[#20FCF1] mb-2 block">Supervisor / Team Leader Name (Print)</label>
                  <input type="text" v-model="formData.signOff.supervisorName" class="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white font-bold uppercase" />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                <div class="bg-white rounded-3xl p-6 text-[#0A1630]">
                  <label class="text-[10px] font-black uppercase text-[#0A1630] mb-3 block">Inspector Digital Signature Seal *</label>
                  <div v-if="inspectorSigPreview" class="mb-4 flex justify-center bg-slate-50 p-4 rounded-xl border">
                    <img :src="inspectorSigPreview" class="max-h-20 object-contain grayscale" alt="Inspector Sig" />
                  </div>
                  <Vue3Signature v-else ref="inspectorSigPad" :sigOption="{ penColor: '#0A1630', backgroundColor: 'rgba(255,255,255,0)' }" :h="'160px'" :w="'100%'" class="border border-slate-200 rounded-xl"></Vue3Signature>
                  <div class="mt-4 flex gap-3">
                    <button type="button" @click="saveInspectorSig" v-if="!inspectorSigPreview" class="px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-xl text-[9px] font-black uppercase tracking-widest">Confirm Seal</button>
                    <button type="button" @click="clearInspectorSig" class="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-[9px] font-black uppercase tracking-widest">Clear</button>
                  </div>
                </div>

                <div class="bg-white rounded-3xl p-6 text-[#0A1630]">
                  <label class="text-[10px] font-black uppercase text-[#0A1630] mb-3 block">Supervisor Digital Signature Seal</label>
                  <div v-if="supervisorSigPreview" class="mb-4 flex justify-center bg-slate-50 p-4 rounded-xl border">
                    <img :src="supervisorSigPreview" class="max-h-20 object-contain grayscale" alt="Supervisor Sig" />
                  </div>
                  <Vue3Signature v-else ref="supervisorSigPad" :sigOption="{ penColor: '#0A1630', backgroundColor: 'rgba(255,255,255,0)' }" :h="'160px'" :w="'100%'" class="border border-slate-200 rounded-xl"></Vue3Signature>
                  <div class="mt-4 flex gap-3">
                    <button type="button" @click="saveSupervisorSig" v-if="!supervisorSigPreview" class="px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-xl text-[9px] font-black uppercase tracking-widest">Confirm Seal</button>
                    <button type="button" @click="clearSupervisorSig" class="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-[9px] font-black uppercase tracking-widest">Clear</button>
                  </div>
                </div>
              </div>
            </section>

            <!-- SUBMIT BUTTON -->
            <div class="pt-6">
              <button type="submit" :disabled="isSubmitting" class="w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[28px] text-lg font-black uppercase tracking-widest shadow-xl hover:scale-[1.01] transition-all flex items-center justify-center gap-3">
                <span v-if="isSubmitting" class="animate-spin h-5 w-5 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                <span>{{ isSubmitting ? 'Synchronising Inspection Protocol...' : 'Authorise & Submit Weekly Plant Inspection' }}</span>
              </button>
            </div>

          </form>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { db, storage } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Vue3Signature from 'vue3-signature';
import QRCode from 'qrcode';
import DocumentUploader from '@/components/DocumentUploader.vue';
import { ShieldCheckIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  assetId: { type: String, default: '' }
});

const router = useRouter();
const userStore = useUserStore();
const functions = getFunctions();

const isSubmitting = ref(false);
const qrCodeUrl = ref('');
const formReference = ref('SS-PEI-1-TBC');
const availableAssets = ref([]);

const inspectorSigPad = ref(null);
const inspectorSigPreview = ref(null);
const supervisorSigPad = ref(null);
const supervisorSigPreview = ref(null);
const localFiles = reactive({});

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const checklistItems = [
  'Item is the correct type and suitable for the intended task',
  'No visible damage, cracks, deformation or excessive wear',
  'Guards, safety devices and interlocks present and functional',
  'Cables, hoses, leads and connections are undamaged and secure',
  'Controls, switches and operating mechanisms function correctly',
  'Calibration / PAT / thorough examination certificate is in date',
  'Identification / Asset ID is legible',
  'Operator is competent and authorised to use this item',
  'Required PPE is available and in good condition',
  'Environmental controls considered (spill, noise, dust, etc.)',
  'Manufacturer instructions / safe system of work available',
  'Item is clean, dry and free from contamination that could affect safety'
];

const formData = reactive({
  itemDetails: {
    assetId: props.assetId || '',
    weekCommencing: new Date().toISOString().slice(0, 10),
    description: '',
    serialNo: '',
    manufacturer: '',
    category: 'Survey',
    siteLocation: '',
    riskRating: 'Low',
    primaryInspector: '',
    competenceConfirmed: true
  },
  checklist: checklistItems.map(() => ({ mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false, comments: '' })),
  dailyResults: { mon: 'PASS', tue: 'PASS', wed: 'PASS', thu: 'PASS', fri: 'PASS', sat: 'PASS', sun: 'PASS' },
  defectsDetails: '',
  signOff: { inspectorName: '', supervisorName: '', inspectorSig: '', supervisorSig: '' }
});

const fetchClientAssets = async () => {
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) return;
    const docRef = doc(db, 'formOptions', 'plantEquipmentItems');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data[cid] && Array.isArray(data[cid])) {
        availableAssets.value = data[cid];
      } else if (data.global && Array.isArray(data.global)) {
        availableAssets.value = data.global;
      }
    }
  } catch (err) {
    console.error("Failed to load client asset options:", err);
  }
};

const onAssetSelected = () => {
  const selected = availableAssets.value.find(a => a.value === formData.itemDetails.assetId);
  if (selected) {
    formData.itemDetails.description = selected.text || '';
  }
};

const saveInspectorSig = () => {
  if (inspectorSigPad.value) {
    const data = inspectorSigPad.value.save('image/png');
    if (data) {
      inspectorSigPreview.value = data;
      formData.signOff.inspectorSig = data;
    }
  }
};
const clearInspectorSig = () => {
  if (inspectorSigPad.value) inspectorSigPad.value.clear();
  inspectorSigPreview.value = null;
  formData.signOff.inspectorSig = '';
};

const saveSupervisorSig = () => {
  if (supervisorSigPad.value) {
    const data = supervisorSigPad.value.save('image/png');
    if (data) {
      supervisorSigPreview.value = data;
      formData.signOff.supervisorSig = data;
    }
  }
};
const clearSupervisorSig = () => {
  if (supervisorSigPad.value) supervisorSigPad.value.clear();
  supervisorSigPreview.value = null;
  formData.signOff.supervisorSig = '';
};

const handleFileChange = (key, files) => { localFiles[key] = Array.from(files); };

const submitForm = async () => {
  if (!formData.signOff.inspectorSig) {
    alert("Inspector Digital Signature Seal is required.");
    return;
  }
  isSubmitting.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) throw new Error("Client context missing.");

    let defectUrls = [];
    if (localFiles.defectEvidence) {
      defectUrls = await Promise.all(localFiles.defectEvidence.map(async (file) => {
        const sRef = storageRef(storage, `clients/${cid}/plantInspections/${Date.now()}_${file.name}`);
        await uploadBytes(sRef, file);
        return getDownloadURL(sRef);
      }));
    }

    const result = await httpsCallable(functions, 'submitGenericForm')({
      formIdentifier: 'singleItemInspectionForm',
      targetCollection: 'healthsafetyFormSubmissions',
      submissionData: {
        ...formData,
        defectEvidenceUrls: defectUrls,
        clientId: cid,
        userId: userStore.userProfile?.userId || null,
        userName: formData.signOff.inspectorName || 'Inspector',
        submittedAt: new Date().toISOString(),
        status: 'Pending Review',
        formType: 'plantInspection'
      }
    });

    if (result.data?.success) {
      router.push({ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: result.data.id } });
    } else {
      throw new Error(result.data?.message || "Submission failed.");
    }
  } catch (err) {
    alert("Submission Error: " + err.message);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  if (userStore.isAuthReady) {
    await fetchClientAssets();
  } else {
    const unwatch = userStore.$subscribe(async () => {
      if (userStore.isAuthReady) {
        await fetchClientAssets();
        unwatch();
      }
    });
  }

  if (props.assetId) {
    formData.itemDetails.assetId = props.assetId;
    onAssetSelected();
  }

  if (userStore.userProfile && !formData.signOff.inspectorName) {
    formData.signOff.inspectorName = `${userStore.userProfile.firstName} ${userStore.userProfile.lastName}`;
  }
  try {
    qrCodeUrl.value = await QRCode.toDataURL(window.location.href, { width: 150, margin: 1 });
    formReference.value = `SS-PEI-1-${Math.floor(1000 + Math.random() * 9000)}`;
  } catch (e) { /* silent */ }
});
</script>

<style scoped>
.form-input { @apply block w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-[#0A1630] shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all text-left; }
.form-label { @apply block text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2 ml-1 text-left; }
</style>